﻿<script setup>
import { ref, computed } from 'vue'
import { StarIcon } from '@heroicons/vue/24/solid'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/vue/24/outline'

const props = defineProps({
    testimonials: {
        type: Array,
        required: true,
    },
})

const initialCount = 4
const showAll = ref(false)
const sectionRef = ref(null)
const hasMore = computed(() => props.testimonials.length > initialCount)
const visibleTestimonials = computed(() =>
    showAll.value ? props.testimonials : props.testimonials.slice(0, initialCount),
)

function collapse() {
    showAll.value = false
    sectionRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}
</script>

<template>
    <section v-if="testimonials.length" ref="sectionRef">
        <div class="mx-auto max-w-2xl text-center">
            <p class="text-brand-primary text-sm font-semibold tracking-wider uppercase">Reference</p>
            <h2 class="heading-2 mt-2">Co o nás říkají naši zákazníci</h2>
            <p class="paragraph mt-4">
                Spokojenost našich zákazníků je pro nás tou nejlepší odměnou. Přečtěte si jejich zkušenosti.
            </p>
        </div>

        <div class="mx-auto mt-12 grid max-w-7xl grid-cols-1 gap-6 lg:grid-cols-2">
            <div
                v-for="(testimonial, index) in visibleTestimonials"
                :key="index"
                class="relative rounded-2xl bg-gray-50 p-8 ring-1 ring-gray-100 transition hover:bg-white hover:shadow-lg hover:ring-gray-200"
            >
                <!-- Stars -->
                <div class="mb-4 flex gap-1">
                    <StarIcon
                        v-for="i in 5"
                        :key="i"
                        class="size-5"
                        :class="i <= testimonial.stars_count ? 'text-brand-primary' : 'text-gray-200'"
                    />
                </div>

                <blockquote v-if="testimonial.text" class="text-gray-700">
                    "{{ testimonial.text }}"
                </blockquote>

                <div class="mt-6 flex items-center gap-4">
                    <img
                        v-if="testimonial.avatar"
                        :src="testimonial.avatar"
                        :alt="testimonial.author_name"
                        class="size-12 rounded-full object-cover"
                    />
                    <div
                        v-else
                        class="bg-brand-primary/10 text-brand-primary flex size-12 items-center justify-center rounded-full text-lg font-bold"
                    >
                        {{ testimonial.author_name.charAt(0) }}
                    </div>
                    <div>
                        <div class="font-semibold text-gray-900">{{ testimonial.author_name }}</div>
                        <div v-if="testimonial.authored_at" class="text-sm text-gray-500">
                            {{ testimonial.authored_at }}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="hasMore && !showAll" class="mt-8 text-center">
            <button
                type="button"
                class="text-brand-primary hover:text-brand-primary/80 inline-flex items-center gap-2 font-semibold transition"
                @click="showAll = true"
            >
                Zobrazit všechny recenze ({{ testimonials.length }})
                <ChevronDownIcon class="size-5" />
            </button>
        </div>

        <div
            v-if="hasMore && showAll"
            class="sticky bottom-4 z-10 mt-8 text-center"
        >
            <button
                type="button"
                class="bg-brand-primary hover:bg-brand-primary/90 inline-flex items-center gap-2 rounded-full px-6 py-3 font-semibold text-white shadow-lg transition"
                @click="collapse"
            >
                Zobrazit méně
                <ChevronUpIcon class="size-5" />
            </button>
        </div>
    </section>
</template>
